import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
} from "@mui/material";
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  getDocs,
} from "firebase/firestore"; // Assuming you're using Firebase

import SaveIcon from "@mui/icons-material/Save";
import useData from "../hooks/useData";

const EditForm = ({ schoolId, onUpdate }) => {
  const [users, setUsers] = useState([]);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [schoolInfo, setSchoolInfo] = useState({
    school_name: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    county: "",
    district: "",
    main_contact: "",
    main_phone: "",
    main_email: "",
    second_contact: "",
    second_email: "",
    pathway: "",
    operations: "",
    users: "",
  });
  const [loadingInfo, setLoadingInfo] = useState(false);

  const { fetchSchoolinfo, updateSchoolInfo } = useData();

  useEffect(() => {
    if (schoolId) {
      fetchSchoolinfo(schoolId)
        .then((data) => setSchoolInfo(data))
        .catch(console.error);
    }
    const fetchUsers = async () => {
      const db = getFirestore();
      const q = query(
        collection(db, "users"),
        where("ccpAdminFlag", "==", true),
        orderBy("name")
      );
      const querySnapshot = await getDocs(q);
      const usersList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        name: doc.data().name,
      }));
      setUsers(usersList);
    };
    fetchUsers();
  }, [schoolId]);

  const handleInfoChange = (e) => {
    const { name, value } = e.target;
    setSchoolInfo((prevInfo) => ({
      ...prevInfo,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoadingInfo(true);
    try {
      const schoolInfoData = {
        jsStruct: JSON.stringify(schoolInfo),
      };
      await updateSchoolInfo(schoolId, schoolInfoData);

      setSnackbarMessage("School info has been updated.");
      setSnackbarOpen(true);
      onUpdate(); // Call the onUpdate function to refresh the data and close the dialog
    } catch (error) {
      console.error("Error updating info:", error);
    } finally {
      setLoadingInfo(false);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <TextField
          label="School"
          name="school_name"
          value={schoolInfo.school_name}
          onChange={handleInfoChange}
          fullWidth
          required
          margin="normal"
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          label="Address"
          name="address"
          value={schoolInfo.address}
          onChange={handleInfoChange}
          fullWidth
          margin="normal"
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          label="City"
          name="city"
          value={schoolInfo.city}
          onChange={handleInfoChange}
          fullWidth
          margin="normal"
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          label="State"
          name="state"
          value={schoolInfo.state}
          onChange={handleInfoChange}
          fullWidth
          margin="normal"
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          label="Zip"
          name="zip"
          value={schoolInfo.zip}
          onChange={handleInfoChange}
          fullWidth
          margin="normal"
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          label="County"
          name="county"
          value={schoolInfo.county}
          onChange={handleInfoChange}
          fullWidth
          margin="normal"
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          label="District"
          name="district"
          value={schoolInfo.district}
          onChange={handleInfoChange}
          fullWidth
          margin="normal"
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          label="Main Contact"
          name="main_contact"
          value={schoolInfo.main_contact}
          onChange={handleInfoChange}
          fullWidth
          margin="normal"
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          label="Main Phone"
          name="main_phone"
          value={schoolInfo.main_phone}
          onChange={handleInfoChange}
          fullWidth
          margin="normal"
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          label="Main Email"
          name="main_email"
          value={schoolInfo.main_email}
          onChange={handleInfoChange}
          fullWidth
          margin="normal"
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          label="Second Contact"
          name="second_contact"
          value={schoolInfo.second_contact}
          onChange={handleInfoChange}
          fullWidth
          margin="normal"
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          label="Second Email"
          name="second_email"
          value={schoolInfo.second_email}
          onChange={handleInfoChange}
          fullWidth
          margin="normal"
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <FormControl fullWidth margin="normal">
          <InputLabel id="pathway-label">Pathway</InputLabel>
          <Select
            labelId="pathway-label"
            value={schoolInfo.pathway}
            name="pathway"
            onChange={handleInfoChange}
            label="Pathway"
          >
            {users.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                {user.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={4}>
        <FormControl fullWidth margin="normal">
          <InputLabel id="operations-label">Operations</InputLabel>
          <Select
            labelId="operations-label"
            value={schoolInfo.operations}
            name="operations"
            onChange={handleInfoChange}
            label="Operations"
          >
            {users.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                {user.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} container justifyContent="center">
        <Grid item xs={4}>
          <Button
            onClick={handleSubmit}
            color="success"
            variant="contained"
            disabled={loadingInfo}
            startIcon={<SaveIcon />}
            sx={{
              mt: 2,
              color: "white",
              bgcolor: "success.main",
              "&:hover": { bgcolor: "success.dark" },
            }}
          >
            {loadingInfo ? "Updating School..." : "Update School"}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default EditForm;
